import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Building2,
  CarFront,
  ChevronRight,
  Clock,
  Eye,
  FileCheck2,
  Files,
  RefreshCw,
  Search,
  ShieldCheck,
  UserCheck,
  XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import ReviewDrawer, { ReviewDrawerById, docLabel } from '../components/ReviewDrawer';
import { useAuth } from '../App';
import { useReviewInbox } from '../components/ReviewInboxContext';
import { lifecycleColor, lifecycleLabel, queueViewState } from '../lib/docReviewLogic.mjs';
import { sameId } from '../lib/idUtil.mjs';
import { ErrorBox } from '../ui/kit';
import './Documents.css';
import PageHeader from '../components/PageHeader';
import { nurDatum } from '../lib/zeit';

// DOC-LIFECYCLE-001: Die Queue zeigt latest/effective getrennt, entscheidet nur im
// geschützten Drawer und gibt für private Assets niemals tokenisierte Links aus.

const STATUS_FILTERS = [
  { key: 'pending', label: 'Ausstehend', hint: 'Neue Prüfungen', tone: 'pending' },
  { key: 'approved', label: 'Freigegeben', hint: 'Aktuell wirksam', tone: 'approved' },
  { key: 'rejected', label: 'Abgelehnt', hint: 'Neuer Upload nötig', tone: 'rejected' },
  { key: 'superseded', label: 'Ersetzt', hint: 'Frühere Revisionen', tone: 'superseded' },
  { key: 'expired', label: 'Abgelaufen', hint: 'Erneuerung nötig', tone: 'expired' },
  { key: 'all', label: 'Alle Vorgänge', hint: 'Gesamte Historie', tone: 'all' },
];

const OWNER_FILTERS = [
  { key: 'all', label: 'Alle Typen', hint: 'Gesamte Queue', icon: Files },
  { key: 'driver', label: 'Fahrer', hint: 'Personenbezogen', icon: UserCheck },
  { key: 'partner', label: 'Partner', hint: 'Unternehmensbezogen', icon: Building2 },
  { key: 'vehicle', label: 'Fahrzeuge', hint: 'Flottenbezogen', icon: CarFront },
];

const ownerLabel = (ownerType) => (
  ownerType === 'partner' ? 'Partner' : ownerType === 'vehicle' ? 'Fahrzeug' : 'Fahrer'
);

const normalized = (value) => String(value || '').trim().toLocaleLowerCase('de-DE');

// FAHRER-KI-SPERRE-HANDELN-001 (Owner 05.09.2026): an der Wächter-Sperre HANDELN — Dokument öffnen, Datum prüfen,
// Neu-Upload beim Fahrer anfordern, Frist-Ausnahme erteilen/entziehen — statt nur „Gesperrt" zu lesen.
const LOCK_TEXT = (lock) => {
  if (!lock) return 'Dokument abgelaufen';
  if (lock.kind === 'expired') return `${lock.label || lock.doc_type} abgelaufen${lock.expires_at ? ` (${fmtIso(lock.expires_at)})` : ''}`;
  if (lock.kind === 'compliance') return `${lock.label || lock.doc_type} gesperrt (Compliance)`;
  if (lock.kind === 'admin') return 'gesperrt (Admin)';
  if (lock.kind === 'selfie') return 'gesperrt (Selfie)';
  return 'gesperrt';
};
const fmtIso = (iso) => (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? nurDatum(iso) : (iso || '–'));

function SuspendedDriverRow({ driver, canManage, onOpenDocument, onChanged }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(null);   // 'waiver' | 'revoke' | null
  const [reason, setReason] = useState('');
  const lock = driver.lock || null;
  const waived = !!(driver.waiver && driver.waiver.document_waiver);
  const call = async (label, fn) => {
    setBusy(true);
    try {
      const response = await fn();
      toast.success(response?.data?.message || label);
      setReasonOpen(null); setReason('');
      onChanged();
    } catch (error) { toast.error(error.response?.data?.error || `${label} fehlgeschlagen`); } finally { setBusy(false); }
  };
  const requestReupload = () => call('Neu-Upload angefordert', () => api.post(`/admin/drivers/${driver.id}/document-reupload-request`, {}));
  const grantWaiver = () => {
    if (!reason.trim()) { toast.error('Bitte einen Grund angeben'); return; }
    return call('Ausnahme erteilt', () => api.post(`/admin/drivers/${driver.id}/document-waiver`, { reason: reason.trim() }));
  };
  const revokeWaiver = () => {
    if (!reason.trim()) { toast.error('Bitte einen Grund angeben'); return; }
    return call('Ausnahme entzogen', () => api.delete(`/admin/drivers/${driver.id}/document-waiver`, { data: { reason: reason.trim() } }));
  };
  const documentLock = !!lock && (lock.kind === 'expired' || lock.kind === 'compliance');
  // REVIEW-FIX-001 (Zweiter Kopf): eine Frist-Ausnahme löst NUR den Ablauf (documentLifecycle.expiredMandatoryDoc), nie eine
  // Compliance-Sperre — dort gibt es nur „Dokument öffnen" und „Datum prüfen", keine wirkungslosen Knöpfe mit Nebenwirkung.
  const expiredLock = !!lock && lock.kind === 'expired';
  return (
    <div className="documents-expiry-row is-driver" data-testid={`suspended-driver-${driver.id}`}>
      <span>
        <strong>{driver.name}</strong>
        <small>Fahrer{driver.company_name ? ` · ${driver.company_name}` : ''} · {LOCK_TEXT(lock)}{waived ? ' · Ausnahme aktiv' : ''}</small>
        {canManage && (
          <span className="documents-expiry-actions">
            {documentLock && lock.document_id && <button onClick={() => onOpenDocument(lock.document_id)} disabled={busy}>Dokument öffnen</button>}
            {documentLock && <button onClick={() => navigate(`/documents/ablaufdaten?driver=${driver.id}`)} disabled={busy}>Datum prüfen</button>}
            {expiredLock && <button onClick={requestReupload} disabled={busy}>Neu-Upload anfordern</button>}
            {expiredLock && !waived && reasonOpen !== 'waiver' && <button onClick={() => { setReasonOpen('waiver'); setReason(''); }} disabled={busy}>Frist-Ausnahme …</button>}
            {waived && reasonOpen !== 'revoke' && <button onClick={() => { setReasonOpen('revoke'); setReason(''); }} disabled={busy}>Ausnahme entziehen …</button>}
            {reasonOpen && (
              <span className="documents-expiry-reason">
                <input value={reason} onChange={(event) => setReason(event.target.value)} placeholder={reasonOpen === 'waiver' ? 'Grund der Ausnahme (Pflicht)' : 'Grund des Entzugs (Pflicht)'} aria-label="Grund" />
                <button className="is-primary" onClick={reasonOpen === 'waiver' ? grantWaiver : revokeWaiver} disabled={busy || !reason.trim()}>{reasonOpen === 'waiver' ? 'Ausnahme erteilen' : 'Entziehen'}</button>
                <button onClick={() => { setReasonOpen(null); setReason(''); }} disabled={busy}>Abbrechen</button>
              </span>
            )}
          </span>
        )}
      </span>
      <b>Gesperrt</b>
    </div>
  );
}

function ExpiryOverview() {
  const { can } = useAuth();
  const canManage = !!(can && can('freigaben.manage'));
  const [overview, setOverview] = useState(null);
  const [reviewDocId, setReviewDocId] = useState(null);

  const load = useCallback(() => {
    api.get('/admin/expiry-watch').then((response) => setOverview(response.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!overview) return null;

  const suspended = [
    ...(overview.suspended_partners || []).map((partner) => ({
      key: `p${partner.id}`,
      title: partner.company_name,
      detail: 'Partner · Konzession abgelaufen',
    })),
  ];
  const suspendedDrivers = overview.suspended_drivers || [];
  const warnings = overview.warnings || [];

  if (!suspended.length && !suspendedDrivers.length && !warnings.length) return null;
  const suspendedCount = suspended.length + suspendedDrivers.length;

  return (
    <section className="documents-expiry">
      {reviewDocId && <ReviewDrawerById documentId={reviewDocId} onClose={() => { setReviewDocId(null); load(); }} onDecided={load} />}
      <header>
        <div>
          <span className="documents-section-icon"><Clock size={15} /></span>
          <div>
            <span className="documents-overline">Ablauf-Wächter · nur lesend</span>
            <h2>Operative Dokumentrisiken</h2>
          </div>
        </div>
        <div className="documents-expiry-metrics">
          <span className={suspendedCount ? 'is-critical' : ''}><b>{suspendedCount}</b> automatisch gesperrt</span>
          <span className={warnings.length ? 'is-warning' : ''}><b>{warnings.length}</b> laufen bald ab</span>
        </div>
      </header>

      <div className="documents-expiry-grid">
        <div className={`documents-expiry-panel ${suspendedCount ? 'is-critical' : ''}`}>
          <div className="documents-expiry-panel-title">
            <XCircle size={14} />
            <span><strong>Automatische Sperren</strong><small>Entsperrung nach gültiger Freigabe, korrigiertem Datum oder Frist-Ausnahme</small></span>
          </div>
          {suspendedCount === 0 ? (
            <p className="documents-expiry-empty">Keine automatischen Sperren.</p>
          ) : (
            <>
              {suspended.map((item) => (
                <div className="documents-expiry-row" key={item.key}>
                  <span><strong>{item.title}</strong><small>{item.detail}</small></span>
                  <b>Gesperrt</b>
                </div>
              ))}
              {suspendedDrivers.map((driver) => (
                <SuspendedDriverRow key={`d${driver.id}`} driver={driver} canManage={canManage} onOpenDocument={setReviewDocId} onChanged={load} />
              ))}
            </>
          )}
        </div>

        <div className={`documents-expiry-panel ${warnings.length ? 'is-warning' : ''}`}>
          <div className="documents-expiry-panel-title">
            <AlertTriangle size={14} />
            <span><strong>60-Tage-Warnstaffel</strong><small>Anstehende Abläufe frühzeitig bearbeiten</small></span>
          </div>
          {warnings.length === 0 ? (
            <p className="documents-expiry-empty">Keine Dokumente in der Warnstaffel.</p>
          ) : warnings.slice(0, 8).map((warning, index) => (
            <div className="documents-expiry-row" key={`${warning.owner_type}-${warning.id || index}`}>
              <span>
                <strong>{warning.name}</strong>
                <small>{warning.owner_type === 'driver' && warning.company_name ? `${warning.company_name} · ` : ''}{warning.label} · {warning.expires_at}</small>
              </span>
              <b>{warning.days_left === 0 ? 'Heute' : `${warning.days_left} Tage`}</b>
            </div>
          ))}
          {warnings.length > 8 && <p className="documents-expiry-more">+{warnings.length - 8} weitere Warnungen</p>}
        </div>
      </div>
    </section>
  );
}

export default function Documents() {
  const [searchParams, setSearchParams] = useSearchParams();
  const inbox = useReviewInbox();
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState(searchParams.get('status') || 'pending');
  const [ownerFilter, setOwnerFilter] = useState(searchParams.get('owner_type') || 'all');
  const [search, setSearch] = useState('');
  const [visibleLimit, setVisibleLimit] = useState(25);
  const [drawerDoc, setDrawerDoc] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (filter !== 'all') params.status = filter;
    if (ownerFilter !== 'all') params.owner_type = ownerFilter;
    api.get('/admin/documents', { params })
      .then((response) => {
        setDocs(response.data.documents || []);
        setLoadError(false);
      })
      .catch(() => {
        setDocs([]);
        setLoadError(true);
      });
  }, [filter, ownerFilter]);

  useEffect(() => {
    setVisibleLimit(25);
    load();
  }, [load]);

  useEffect(() => {
    setVisibleLimit(25);
  }, [search]);

  // Deep-Link ?doc=<id> bleibt filterunabhängig und öffnet direkt den geschützten Drawer.
  useEffect(() => {
    const docId = searchParams.get('doc');
    if (!docId) return;
    api.get('/admin/documents').then((response) => {
      const document = (response.data.documents || []).find((item) => sameId(item.id, docId));
      if (document) setDrawerDoc(document);
      else toast.error('Dokument nicht gefunden (evtl. bereits entfernt)');
    }).catch(() => toast.error('Dokument konnte nicht geladen werden'));
  }, [searchParams.get('doc')]);

  const closeDrawer = () => {
    setDrawerDoc(null);
    if (searchParams.get('doc')) {
      searchParams.delete('doc');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const onDecided = (docId) => {
    inbox.documentDecided(docId);
    load();
  };

  const searchTerm = normalized(search);
  const matchingDocs = useMemo(() => {
    if (!searchTerm) return docs;
    return docs.filter((document) => normalized([
      docLabel(document.document_type),
      document.document_type,
      document.owner_name,
      document.owner_email,
      document.display_name,
      document.id,
      document.revision,
      lifecycleLabel(document.lifecycle_status),
      document.rejection_reason,
    ].join(' ')).includes(searchTerm));
  }, [docs, searchTerm]);
  const visibleDocs = matchingDocs.slice(0, visibleLimit);
  const pendingCount = docs.filter((document) => document.lifecycle_status === 'pending_review').length;
  const effectiveCount = docs.filter((document) => Number(document.is_effective) === 1).length;
  const activeStatus = STATUS_FILTERS.find((item) => item.key === filter) || STATUS_FILTERS[0];

  return (
    <div className="documents-page">
      <PageHeader
        title="Dokumentenprüfung"
        description="Private Unterlagen revisionssicher prüfen, freigeben und operativ überwachen."
        actions={
        <div className="documents-heading-actions">
          <span className="is-loaded"><Files size={12} /> {docs.length} geladen</span>
          <span className="is-pending"><Clock size={12} /> {pendingCount} ausstehend</span>
          <span><FileCheck2 size={12} /> {effectiveCount} wirksam</span>
          <button onClick={load}><RefreshCw size={13} /> Aktualisieren</button>
        </div>
      }
      />

      <section className="documents-principle">
        <span><ShieldCheck size={17} /></span>
        <div>
          <strong>Private Assets bleiben im geschützten Prüfbereich</strong>
          <p>Die Queue zeigt ausschließlich Metadaten. Inhalte werden autorisiert als Blob geladen und beim Schließen verworfen.</p>
        </div>
        <i>Kein Token-Link</i>
      </section>

      <section className="documents-workspaces" aria-label="Dokumentenstatus">
        {STATUS_FILTERS.map((item) => (
          <button
            key={item.key}
            className={filter === item.key ? `is-active is-${item.tone}` : ''}
            onClick={() => setFilter(item.key)}
          >
            <span className="documents-status-marker"><i /></span>
            <span><strong>{item.label}</strong><small>{item.hint}</small></span>
            {filter === item.key ? <b>{docs.length}</b> : <ChevronRight size={13} />}
          </button>
        ))}
      </section>

      <ExpiryOverview />

      <section className="documents-controls">
        <div className="documents-owner-filters">
          <span className="documents-overline">Eigentümertyp</span>
          <div>
            {OWNER_FILTERS.map((item) => {
              const Icon = item.icon;
              return (
                <button key={item.key} className={ownerFilter === item.key ? 'is-active' : ''} onClick={() => setOwnerFilter(item.key)}>
                  <Icon size={13} />
                  <span><strong>{item.label}</strong><small>{item.hint}</small></span>
                </button>
              );
            })}
          </div>
        </div>

        <label className="documents-search">
          <span>Dokument, Eigentümer, E-Mail oder Prüf-ID</span>
          <div><Search size={14} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Geladene Queue lokal durchsuchen …" /></div>
        </label>

        <div className="documents-result-stat"><small>Treffer</small><strong>{matchingDocs.length}</strong><span>in geladener Queue</span></div>
        <div className="documents-result-stat"><small>Sichtbar</small><strong>{Math.min(visibleLimit, matchingDocs.length)}</strong><span>zunächst max. 25</span></div>
      </section>

      <section className="documents-directory">
        <header>
          <div>
            <span className="documents-overline">Dokumenten-Lifecycle · {activeStatus.label}</span>
            <h2>{activeStatus.label === 'Alle Vorgänge' ? activeStatus.label : `${activeStatus.label}e Dokumente`}</h2>
          </div>
          <span><ShieldCheck size={12} /> Entscheidung ausschließlich im Prüfbereich</span>
        </header>

        {queueViewState(docs, loadError) === 'error' ? (
          <div className="documents-error-wrap">
            <ErrorBox text="Dokumente konnten nicht geladen werden — die Queue ist NICHT leer, sondern nicht erreichbar." onRetry={load} />
          </div>
        ) : (
          <div className="documents-table-scroll">
            <table className="documents-table">
              <thead>
                <tr>{['Typ', 'Eigentümer', 'Revision', 'Datei', 'Status', 'Ablaufdatum', 'Hochgeladen', 'Aktionen'].map((heading) => <th key={heading}>{heading}</th>)}</tr>
              </thead>
              <tbody>
                {visibleDocs.length === 0 && (
                  <tr>
                    <td colSpan={8} className="documents-empty">
                      <Files size={22} />
                      <strong>Keine Dokumente gefunden</strong>
                      <span>{searchTerm ? 'Suchbegriff anpassen oder zurücksetzen.' : 'Neue Vorgänge erscheinen hier automatisch.'}</span>
                    </td>
                  </tr>
                )}
                {visibleDocs.map((document) => {
                  const lifecycle = lifecycleColor(document.lifecycle_status);
                  return (
                    <tr key={document.id}>
                      <td><span className="documents-type">{docLabel(document.document_type)}</span></td>
                      <td>
                        <div className="documents-owner">
                          <span>{normalized(document.owner_name).split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || '–'}</span>
                          <div><strong>{document.owner_name || '–'}</strong><small>{document.owner_email || 'Keine E-Mail'} · {ownerLabel(document.owner_type)}</small></div>
                        </div>
                      </td>
                      <td>
                        <div className="documents-revision">
                          <strong>r{document.revision || 1}</strong>
                          {Number(document.is_effective) === 1 && <span>Wirksam</span>}
                        </div>
                      </td>
                      <td>
                        {document.has_private_asset ? (
                          <button className="documents-private-file" onClick={() => setDrawerDoc(document)}>
                            <ShieldCheck size={13} /><span>{document.display_name || 'Geschützt ansehen'}</span>
                          </button>
                        ) : <span className="documents-no-file">Kein Inhalt</span>}
                      </td>
                      <td>
                        <span className="documents-lifecycle" style={{ color: lifecycle.color, background: lifecycle.bg }}>
                          <i /> {lifecycleLabel(document.lifecycle_status)}
                        </span>
                        {document.lifecycle_status === 'rejected' && document.rejection_reason && <small className="documents-rejection">{document.rejection_reason}</small>}
                      </td>
                      <td><span className="documents-date">{nurDatum(document.expires_at, { leer: '–' })}</span></td>
                      <td><span className="documents-date">{nurDatum(document.created_at)}</span></td>
                      <td>
                        <button className={document.lifecycle_status === 'pending_review' ? 'documents-review-action' : 'documents-detail-action'} onClick={() => setDrawerDoc(document)}>
                          <Eye size={12} /> {document.lifecycle_status === 'pending_review' ? 'Prüfen' : 'Ansehen'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loadError && matchingDocs.length > visibleDocs.length && (
          <footer className="documents-load-more">
            <span>{visibleDocs.length} von {matchingDocs.length} sichtbar</span>
            <button onClick={() => setVisibleLimit((limit) => Math.min(limit + 25, matchingDocs.length))}>
              Weitere {Math.min(25, matchingDocs.length - visibleDocs.length)} anzeigen
            </button>
          </footer>
        )}
      </section>

      {/* key={id}: Ein Dokumentwechsel remountet den Drawer und verwirft alte Blob-URLs. */}
      {drawerDoc && <ReviewDrawer key={drawerDoc.id} doc={drawerDoc} onClose={closeDrawer} onDecided={onDecided} />}
    </div>
  );
}
